import { useCallback, useMemo } from "react";
import { throttle } from "lodash";
import useAnnotatorConfig from "./useAnnotatorConfig";
import { useAppDispatch, useAppSelector } from "../../app";
import selectPdfNumberOfPages from "../../document/selectors/selectPdfNumberOfPage";
import { rescalePdfPages } from "../../document/store/documentSlice";
import { MIN_SCALE } from "../../annotation/constants";
import { clearGeometryCache } from "../../annotation/selectors/tokensGeometrySelector";

const useControls = () => {
  const dispatch = useAppDispatch();

  const pages = useAppSelector(selectPdfNumberOfPages);

  const { currentPage, setCurrentPage, scale, setScale } = useAnnotatorConfig();

  // `useMemo` rather than `useCallback` so the `throttle()` call is the memoized
  // expression instead of an argument that is re-evaluated (and thrown away) on
  // every render. The returned instance is the same one `useCallback` handed
  // back before: the throttled function created on the render where the deps
  // last changed. `throttle` starts no timers at creation time, so dropping the
  // per-render throwaway instances is not observable.
  const incrementScale = useMemo(
    () =>
      throttle((increment: number) => {
        const newScale = Math.max(
          Math.round((scale + increment) * 10) / 10,
          MIN_SCALE,
        );

        setScale(newScale);
        dispatch(rescalePdfPages({ oldScale: scale, newScale }));
        clearGeometryCache();
      }, 250),
    [setScale, scale, dispatch],
  );

  const handleScaleChange = useCallback(
    (targetScale: number) => {
      const newScale = Math.max(Math.round(targetScale * 10) / 10, MIN_SCALE);

      setScale(newScale);
      dispatch(rescalePdfPages({ oldScale: scale, newScale }));
      clearGeometryCache();
    },
    [setScale, scale, dispatch],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (page > pages || page <= 0) {
        return;
      }

      setCurrentPage(page);
    },
    [pages, setCurrentPage],
  );

  return {
    scale,
    handleScaleChange,
    incrementScale,
    currentPage,
    handlePageChange,
    pages,
  };
};

export default useControls;
